import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchX, SlidersHorizontal } from 'lucide-react';
import { CATEGORIES, DISTRICTS } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { ProductCard } from '../components/commerce/ProductCard';
import { EmptyState } from '../components/commerce/EmptyState';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';

const SORTS = [
  { id: 'relevant', label: 'Relevant' },
  { id: 'popular', label: 'Popular' },
  { id: 'latest', label: 'Latest' },
  { id: 'price-asc', label: 'Price: Low to High' },
  { id: 'price-desc', label: 'Price: High to Low' },
];

function Filters({ filters, setFilters }) {
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  return (
    <div className="flex flex-col gap-6" data-testid="search-filters">
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-kelate-soft">Category</h4>
        <select
          data-testid="filter-category"
          value={filters.category}
          onChange={(e) => set({ category: e.target.value })}
          className="w-full rounded-sm border border-kelate-line bg-kelate-surface px-2 py-2 text-sm"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-kelate-soft">Location</h4>
        <select data-testid="filter-location" value={filters.district} onChange={(e) => set({ district: e.target.value })} className="w-full rounded-sm border border-kelate-line bg-kelate-surface px-2 py-2 text-sm">
          <option value="">All districts</option>
          {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-kelate-soft">Price</h4>
        <div className="flex items-center gap-2">
          <input data-testid="filter-price-min" type="number" placeholder="Min" value={filters.min} onChange={(e) => set({ min: e.target.value })} className="w-full rounded-sm border border-kelate-line bg-kelate-surface px-2 py-2 text-sm" />
          <span className="text-kelate-soft">-</span>
          <input data-testid="filter-price-max" type="number" placeholder="Max" value={filters.max} onChange={(e) => set({ max: e.target.value })} className="w-full rounded-sm border border-kelate-line bg-kelate-surface px-2 py-2 text-sm" />
        </div>
      </div>
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-kelate-soft">Rating</h4>
        <select data-testid="filter-rating" value={filters.rating} onChange={(e) => set({ rating: e.target.value })} className="w-full rounded-sm border border-kelate-line bg-kelate-surface px-2 py-2 text-sm">
          <option value="">Any rating</option>
          <option value="4.5">4.5 & up</option>
          <option value="4.8">4.8 & up</option>
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm text-kelate-ink">
        <input data-testid="filter-sale" type="checkbox" checked={filters.sale} onChange={(e) => set({ sale: e.target.checked })} className="accent-kelate-darkwood" />
        On promotion
      </label>
      <label className="flex items-center gap-2 text-sm text-kelate-ink">
        <input data-testid="filter-local" type="checkbox" checked={filters.local} onChange={(e) => set({ local: e.target.checked })} className="accent-kelate-darkwood" />
        Made in Kelantan
      </label>
    </div>
  );
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const sort = params.get('sort') || 'relevant';
  const [filters, setFilters] = useState({
    category: params.get('category') || '', district: '', min: '', max: '', rating: '', sale: false, local: false,
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const { customProducts } = useApp();

  const results = useMemo(() => {
    let r = customProducts.filter((p) => {
      if (q && !`${p.name} ${p.category}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (filters.category && p.category !== filters.category) return false;
      if (filters.district && p.district !== filters.district) return false;
      if (filters.min && p.price < Number(filters.min)) return false;
      if (filters.max && p.price > Number(filters.max)) return false;
      if (filters.rating && p.rating < Number(filters.rating)) return false;
      if (filters.sale && !(p.badges || []).includes('SALE')) return false;
      if (filters.local && !p.madeInKelantan) return false;
      return true;
    });
    if (sort === 'popular') r = [...r].sort((a, b) => b.sold - a.sold);
    if (sort === 'price-asc') r = [...r].sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') r = [...r].sort((a, b) => b.price - a.price);
    if (sort === 'latest') r = [...r].sort((a, b) => ((b.badges || []).includes('NEW') ? 1 : 0) - ((a.badges || []).includes('NEW') ? 1 : 0));
    return r;
  }, [q, filters, sort, customProducts]);

  return (
    <div data-testid="search-page" className="mx-auto max-w-[1280px] px-4 py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-kelate-darkwood sm:text-3xl">
            {q ? `Results for "${q}"` : 'Browse all products'}
          </h1>
          <p className="text-sm text-kelate-soft" data-testid="search-result-count">{results.length} products</p>
        </div>
        <div className="flex items-center gap-2">
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-filter-btn" className="flex items-center gap-1.5 rounded-sm border border-kelate-line bg-kelate-surface px-3 py-2 text-sm md:hidden">
                <SlidersHorizontal size={14} /> Filters
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-lg bg-kelate-surface px-5 pb-10 pt-6">
              <h3 className="mb-4 font-semibold text-kelate-ink">Filters</h3>
              <Filters filters={filters} setFilters={setFilters} />
              <button data-testid="apply-filters-btn" onClick={() => setSheetOpen(false)} className="mt-6 w-full rounded-sm bg-kelate-darkwood py-2.5 text-sm font-medium text-kelate-surface">
                Show {results.length} results
              </button>
            </SheetContent>
          </Sheet>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setParams((p) => { p.set('sort', e.target.value); return p; })}
            className="rounded-sm border border-kelate-line bg-kelate-surface px-2 py-2 text-sm"
          >
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid gap-8 md:grid-cols-[220px_1fr]">
        <aside className="hidden md:block">
          <div className="sticky top-24 rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
            <Filters filters={filters} setFilters={setFilters} />
          </div>
        </aside>
        <div>
          {results.length === 0 ? (
            <EmptyState icon={SearchX} title="No results" description="Try a different keyword or adjust your filters." testId="search-empty" />
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="search-results">
              {results.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
